import { useEffect, useState } from 'react';
import { useFormik } from 'formik';
// mantine
import {
    Autocomplete,
    Button,
    Switch,
    Select,
    UnstyledButton
} from '@mantine/core';
// utils
import type { TRegistryItemData } from '@newo/api/gateway';
import validationSchema from '@/pages/VersionControl/components/Storages/components/ReleaseForm/ReleaseForm.schema';
import { useRegistriesQuery } from '@/services/designer/registries/registries.queries';
import { useRegistryItemsQuery } from '@/services/designer/registry_items/registry_items.queries';
import { useRegistryItemVersionsQuery } from '@/services/bff/bff.queries';

interface ReleaseFormProps {
    onClose?: () => void;
    onSubmit?: (registryId: string, data: TRegistryItemData) => Promise<void>;
    imageId: string;
}

const ReleaseForm = ({ onClose, onSubmit, imageId }: ReleaseFormProps) => {
    const [selectedRegistryId, setSelectedRegistryId] = useState<
        string | undefined
    >();
    const { data: registries = [] } = useRegistriesQuery();
    const { data: registryItems = [] } =
        useRegistryItemsQuery(selectedRegistryId);

    const initialValues: TRegistryItemData = {
        idn: '',
        version: '',
        project_image_id: imageId,
        is_public: false
    };

    const formik = useFormik({
        initialValues,
        validationSchema,
        async onSubmit(formData) {
            await onSubmit?.(selectedRegistryId ?? '', formData);
            formik.setSubmitting(false);
            onClose?.();
        }
    });

    const registryIdn = registries.find(
        (registry) => registry.id === selectedRegistryId
    )?.idn;
    const { data: versionsResponse } = useRegistryItemVersionsQuery(
        registryIdn,
        formik.values.idn,
        {
            page: 1,
            per: 100
        }
    );
    const versions = versionsResponse?.items ?? [];

    useEffect(() => {
        if (registries.length > 0) {
            const productionRegistryId = registries.find(
                (registry) => registry.idn === 'production'
            )?.id;
            setSelectedRegistryId(productionRegistryId);
        }
    }, [registries]);

    useEffect(() => {
        if (selectedRegistryId) {
            formik.setFieldValue('idn', '');
        }
    }, [selectedRegistryId]);

    const handleCancel = () => {
        onClose?.();
    };

    const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        formik.setFieldValue('is_public', event.target.checked);
    };

    const registryItemOptions = Array.from(
        new Set(registryItems.map((item) => item.idn))
    );
    return (
        <form
            autoComplete="off"
            className="flex flex-col gap-3"
            onSubmit={formik.handleSubmit}
        >
            <div>Release Project Image</div>
            <Select
                clearable
                withAsterisk
                comboboxProps={{ withinPortal: false }}
                data={registries.map((registry) => ({
                    label: registry.idn,
                    value: registry.id
                }))}
                label="Registry"
                name="registry_id"
                placeholder="Select Registry"
                value={selectedRegistryId}
                onChange={(value) => {
                    setSelectedRegistryId(value ?? undefined);
                }}
            />

            <Autocomplete
                clearable
                withAsterisk
                comboboxProps={{ withinPortal: false }}
                data={registryItemOptions}
                error={formik.touched.idn ? formik.errors.idn : undefined}
                label="Module"
                name="idn"
                placeholder="Select module"
                value={formik.values.idn}
                onBlur={formik.handleBlur}
                onChange={(value) => {
                    formik.setFieldValue('idn', value);
                }}
            />

            <Autocomplete
                withAsterisk
                comboboxProps={{ withinPortal: false }}
                data={versions.map((version) => ({
                    label: version.version,
                    value: version.version
                }))}
                disabled={!selectedRegistryId}
                error={
                    formik.touched.version && formik.values.version
                        ? formik.errors.version
                        : undefined
                }
                label="Release Version"
                name="version"
                placeholder="1.0.0"
                value={formik.values.version}
                onChange={(value) => {
                    formik.setFieldValue('version', value);
                }}
            />

            <Switch
                checked={formik.values.is_public}
                disabled={!selectedRegistryId}
                label="Deploy"
                name="is_public"
                onChange={handleSwitchChange}
            />

            <div className="flex justify-between gap-2">
                <UnstyledButton
                    className="text-xs text-gray-6"
                    onClick={handleCancel}
                >
                    Cancel
                </UnstyledButton>
                <Button
                    disabled={
                        !formik.dirty || !formik.isValid || formik.isSubmitting
                    }
                    size="xs"
                    type="submit"
                >
                    {formik.values.is_public ? 'Release and Deploy' : 'Release'}
                </Button>
            </div>
        </form>
    );
};

export default ReleaseForm;
